import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MODELOS_PROMOCAO, modeloPorNome, type ModeloPromocao } from "../promocoes-modelos";
import { IconClose, IconTrendUp, IconCheck } from "../icons";

/**
 * MODELO DE PROMOÇÃO por SELEÇÃO — a forma pela qual o colaborador está sendo
 * promovido (ver src/promocoes-modelos.ts).
 *
 * Mesma ideia do campo de cargo: em vez de digitar, quem registra MARCA o
 * caminho na lista da política da empresa. Assim o histórico de promoções pode
 * ser lido depois por modelo ("quantas foram por mérito?", "quantas efetivações
 * do trimestre?") e ninguém inventa um critério novo na hora de promover.
 *
 * Grava só o NOME do modelo num campo de texto.
 */

interface Props {
  value: string;
  onChange: (modelo: string) => void;
  disabled?: boolean;
}

export default function ModeloPromocaoInput({ value, onChange, disabled }: Props) {
  const [aberto, setAberto] = useState(false);
  const modelo = modeloPorNome(value);

  return (
    <>
      <div className="cargo-campo">
        <button
          type="button"
          className="cargo-botao"
          disabled={disabled}
          onClick={() => setAberto(true)}
          title="Escolher como este colaborador está sendo promovido"
        >
          <IconTrendUp size={16} />
          <span className={value ? "cargo-valor" : "cargo-placeholder"}>
            {value || "Clique para escolher o modelo de promoção…"}
          </span>
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="icon-btn"
            title="Limpar modelo"
            onClick={() => onChange("")}
          >
            <IconClose size={15} />
          </button>
        )}
      </div>

      {/* O que este caminho significa, sem precisar abrir o popup de novo. */}
      {modelo && (
        <p className="modelo-dica">
          {modelo.efeito} <span className="muted">· {modelo.aprovacao}</span>
        </p>
      )}

      {aberto && (
        <ModeloPicker
          atual={value}
          onFechar={() => setAberto(false)}
          onConfirmar={(m) => {
            onChange(m.nome);
            setAberto(false);
          }}
        />
      )}
    </>
  );
}

interface PickerProps {
  atual: string;
  onConfirmar: (modelo: ModeloPromocao) => void;
  onFechar: () => void;
}

function ModeloPicker({ atual, onConfirmar, onFechar }: PickerProps) {
  const [marcado, setMarcado] = useState<ModeloPromocao | null>(modeloPorNome(atual));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFechar();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFechar]);

  return createPortal(
    // Igual ao popup de cargo: o clique não pode subir até o overlay do
    // formulário que está atrás (senão o formulário fecha junto).
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        e.stopPropagation();
        onFechar();
      }}
    >
      <div className="modal-card cargo-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconTrendUp size={18} /> Como este colaborador está sendo promovido?
          </h3>
          <button type="button" className="icon-btn" onClick={onFechar}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="modelo-lista">
            {MODELOS_PROMOCAO.map((m) => (
              <label
                key={m.key}
                className={`modelo-item ${marcado?.key === m.key ? "marcada" : ""}`}
              >
                <input
                  type="radio"
                  name="modelo-promocao"
                  checked={marcado?.key === m.key}
                  onChange={() => setMarcado(m)}
                />
                <div className="modelo-texto">
                  <strong>{m.nome}</strong>
                  <span className="modelo-resumo">{m.resumo}</span>
                  <span className="modelo-linha">
                    <span className="modelo-tag">Efeito</span> {m.efeito}
                  </span>
                  <span className="modelo-linha">
                    <span className="modelo-tag">Reajuste</span> {m.reajuste}
                    <span className="modelo-tag">Carência</span> {m.carencia}
                  </span>
                  <span className="modelo-linha">
                    <span className="modelo-tag">Aprovação</span> {m.aprovacao}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="modal-foot cargo-foot">
          <span className="cargo-marcado">
            {marcado ? (
              <>
                Selecionado: <strong>{marcado.nome}</strong>
              </>
            ) : (
              `${MODELOS_PROMOCAO.length} formas de promover`
            )}
          </span>
          <button type="button" className="btn-secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!marcado}
            onClick={() => marcado && onConfirmar(marcado)}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Critérios do modelo escolhido, por marcação. É a "régua" daquele caminho:
 * o RH marca o que já está cumprido e o registro guarda a lista marcada, para
 * a promoção poder ser explicada meses depois.
 *
 * Guardado como texto, um critério por linha — a redação do catálogo pode
 * mudar sem alterar o que já foi registrado.
 */
export function CriteriosPromocaoInput({
  modeloNome,
  value,
  onChange,
}: {
  modeloNome: string;
  value: string;
  onChange: (texto: string) => void;
}) {
  const modelo = modeloPorNome(modeloNome);
  const marcados = String(value ?? "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  if (!modelo) {
    return (
      <p className="modelo-dica">
        Escolha o modelo de promoção acima para ver os critérios daquele caminho.
      </p>
    );
  }

  function alternar(criterio: string) {
    const jaTem = marcados.includes(criterio);
    const next = jaTem ? marcados.filter((c) => c !== criterio) : [...marcados, criterio];
    // Mantém a ordem do catálogo, não a ordem dos cliques.
    const ordenado = modelo!.criterios.filter((c) => next.includes(c));
    // Critério de registro antigo (texto que saiu do catálogo) não se perde.
    const fora = next.filter((c) => !modelo!.criterios.includes(c));
    onChange([...ordenado, ...fora].join("\n"));
  }

  const total = modelo.criterios.length;
  const feitos = modelo.criterios.filter((c) => marcados.includes(c)).length;

  return (
    <div className="criterios-box">
      <div className="criterios-head">
        <span>
          Critérios de <strong>{modelo.nome}</strong>
        </span>
        <span className={`badge ${feitos === total ? "badge-ok" : ""}`}>
          {feitos}/{total} cumpridos
        </span>
      </div>

      <div className="criterios-lista">
        {modelo.criterios.map((c) => (
          <label key={c} className={`criterio ${marcados.includes(c) ? "marcado" : ""}`}>
            <input
              type="checkbox"
              checked={marcados.includes(c)}
              onChange={() => alternar(c)}
            />
            <span>{c}</span>
          </label>
        ))}
      </div>

      <div className="criterios-evidencias">
        <IconCheck size={14} />
        <span>
          <strong>Comprovar com:</strong> {modelo.evidencias.join(" · ")}
        </span>
      </div>
    </div>
  );
}
